import axios from '../common/axios';
import { Place, User } from './model';
import debounce from 'debounce-promise';

// Type for adding user roles.
export type AddUserRoles = {
  UserId: number;
  RoleIds: number[];
};

// Type for removing a user role.
export type RevoveUserRole = {
  UserId: number;
  RoleId: number;
};

// Type for updating user status.
export type UserStatusUpdateRequest = {
  userId: number;
  isActive: boolean;
  isAdmin: boolean;
};
export enum UserColumns {
  IsActive = 'isActive',
  IsAdmin = 'isAdmin',
}

// Fetch all users.
async function getAllUsers(): Promise<User[]> {
  return axios.get('user/users').then((res) => res.data);
}

// Fetch the profile of the currently authenticated user.
async function getProfile(): Promise<any> {
  return axios.get('user').then((res) => res.data);
}
/**
 * Add a new user.
 * @param userProfile - The profile of the user to add.
 */
async function addUser(userProfile: Partial<any>): Promise<any> {
  return axios.post<any>('user', userProfile).then((res) => res.data);
}

// Fetch the places associated with the currently authenticated user.
async function getUserPlaces(): Promise<Place[]> {
  return axios.get('user/places').then((res) => res.data);
}
/**
 * Fetch a user by their ID.
 * @param id - The ID of the user to fetch.
 */
async function getUserById(id: string): Promise<User> {
  return axios.get(`user/${id}`).then((res) => res.data);
}
/**
 * Add roles to a user.
 * @param addRoles - The information containing the user ID and role IDs to add.
 */
async function addUserRoles(addRoles: AddUserRoles): Promise<AddUserRoles> {
  return axios.post<AddUserRoles>('user/roles', addRoles).then((res) => res.data);
}
/**
 * Remove a user role.
 * @param removeUserRole - The information containing the user ID and role ID to remove.
 */
async function removeUserRole(revoveUserRole: RevoveUserRole): Promise<AddUserRoles> {
  return axios.delete<AddUserRoles>('user/role', { data: revoveUserRole }).then((res) => res.data);
}

/**
 * Update the status of a user.
 * @param statusUpdate - The information containing the user ID, active status, and admin status.
 */
async function userUpdateStatus(statusUpdate: UserStatusUpdateRequest): Promise<any> {
  return axios.patch(`user/${statusUpdate.userId}/updateStatus`, statusUpdate).then((res) => res.data);
}

// Fetch the status of the currently authenticated user.
async function getUserStatus(): Promise<{ isActive: boolean; isAdmin: boolean }> {
  return axios.get('user/status').then((res) => res.data);
}

// Get user settings for a given key.
async function getUserSettings(key: string): Promise<any> {
  return axios.get(`user/settings/${key}`).then((res) => res.data);
}

// Save or update user settings.
async function saveUserSettings(data: any): Promise<any> {
  return axios.post('user/settings', data).then((res) => res.data);
}

export {
  getAllUsers,  
  getProfile,
  addUser,
  getUserPlaces,
  getUserById,
  addUserRoles,
  removeUserRole,
  userUpdateStatus,
  getUserStatus,
  getUserSettings,
  saveUserSettings,
};
